import { plural, t, Trans } from "@lingui/macro";
import React from "react";
import { pageContext } from "../../../../../components/PageProvider/PageProvider";
import { mealLabel, weekdayLong } from "../../../../../utils/translations";
import { EnergyLevel, MealPlannerConfirmationMessage, MealPlannerErrorCode, Message } from "../../types";

export function useMealPlannerTranslations() {
  const { locale } = React.useContext(pageContext);

  return React.useMemo(() => {
    const translations = {
      pageTitle: t({
        id: "MealPlanner.pageTitle",
        message: "Meal plan — Diet Doctor",
      }),

      defaultMealPlanTitle: t({
        id: "MealPlanner.defaultMealPlanTitle",
        message: "Unnamed meal plan",
      }),
      heroImageDescription: t({
        id: "MealPlanner.heroImageDescription",
        message: "Meal plan image",
      }),

      currentMealPlanTitle: t({
        id: "MealPlanner.currentMealPlanTitle",
        message: "Current meal plan",
      }),

      toolbar: {
        startMealPlanButtonLabel: t({
          id: "MealPlanner.toolbar.startMealPlanButtonLabel",
          message: "Start",
        }),
        copyMealPlanButtonLabel: t({
          id: "MealPlanner.toolbar.copyMealPlanButtonLabel",
          message: "Add to my meal plans",
        }),
        mealPlanStartedLabel: t({
          id: "MealPlanner.toolbar.mealPlanStartedLabel",
          message: "Current",
        }),
      },

      energyLevel: {
        [EnergyLevel.HIGH_PROTEIN_KETO]: t({
          id: "MealPlanner.energyLevel.highProteinKetoLabel",
          message: "High protein ketogenic",
        }),
        [EnergyLevel.HIGH_PROTEIN_LOW_CARB]: t({
          id: "MealPlanner.energyLevel.highProteinLowCarbLabel",
          message: "High protein moderate low carb",
        }),
        [EnergyLevel.HIGH_PROTEIN_LIBERAL]: t({
          id: "MealPlanner.energyLevel.highProteinLiberalLabel",
          message: "High protein liberal low carb",
        }),
        [EnergyLevel.KETO]: t({
          id: "MealPlanner.energyLevel.ketoLabel",
          message: "Ketogenic",
        }),
        [EnergyLevel.LOW_CARB]: t({
          id: "MealPlanner.energyLevel.lowCarbLabel",
          message: "Moderate low carb",
        }),
        [EnergyLevel.LIBERAL]: t({
          id: "MealPlanner.energyLevel.liberalLabel",
          message: "Liberal low carb",
        }),
      },

      quantities: {
        amountInGrams: (rawAmount: number, decimalPlaces = 2) => {
          const factor = Math.pow(10, Math.max(decimalPlaces, 0));
          const amount = Math.round(rawAmount * factor) / factor;
          return t({
            id: "MealPlanner.quantities.amountInGrams",
            message: `${amount}g`,
          });
        },
        percentage: (rawAmount: number, decimalPlaces = 2) => {
          const factor = Math.pow(10, Math.max(decimalPlaces, 0));
          const amount = Math.round(rawAmount * factor) / factor;
          return t({
            id: "MealPlanner.quantities.percentage",
            message: `${amount}%`,
          });
        },
        timeInMinutes: (amount: number | string) =>
          t({
            id: "MealPlanner.quantities.timeInMinutes",
            message: `${amount} min`,
          }),
        servings: (servings: number) =>
          t({
            id: "MealPlanner.quantities.servings",
            message: plural(servings, {
              one: "# serving",
              other: "# servings",
            }),
          }),
        servingsWithLeftovers: (servings: number, leftoverServings: number) =>
          t({
            id: "MealPlanner.quantities.servingsWithLeftovers",
            message: `${servings} + ${leftoverServings} servings`,
          }),
      },

      dates: {
        weekdayLong: weekdayLong(locale),
      },

      meals: {
        mealLabel,
      },

      mealPlanGrid: {
        addMealButtonLabel: t({
          id: "MealPlanner.mealPlanGrid.addMealButtonLabel",
          message: "Add meal",
        }),
        missingRecipePlaceholderTitle: t({
          id: "MealPlanner.mealPlanGrid.missingRecipePlaceholderTitle",
          message: "Add recipe",
        }),
      },

      nutritionalInfo: {
        nutrients: {
          netCarbs: t({
            id: "MealPlanner.nutritionalInfo.nutrients.netCarbs",
            message: "Net carbs",
          }),
          totalCarbs: t({
            id: "MealPlanner.nutritionalInfo.nutrients.totalCarbs",
            message: "Total carbs",
          }),
          protein: t({
            id: "MealPlanner.nutritionalInfo.nutrients.protein",
            message: "Protein",
          }),
          fat: t({
            id: "MealPlanner.nutritionalInfo.nutrients.fat",
            message: "Fat",
          }),
          fiber: t({
            id: "MealPlanner.nutritionalInfo.nutrients.fiber",
            message: "Fiber",
          }),
          calories: t({
            id: "MealPlanner.nutritionalInfo.nutrients.calories",
            message: "Calories",
          }),
        },

        averageNutritionPerDayLabel: t({
          id: "MealPlanner.nutritionalInfo.averageNutritionPerDayLabel",
          message: "Average nutrition per day",
        }),
        totalNutritionForDayLabel: t({
          id: "MealPlanner.nutritionalInfo.totalNutritionForDayLabel",
          message: "Total nutrition for this day",
        }),
        totalNutritionForRecipe: t({
          id: "MealPlanner.nutritionalInfo.totalNutritionForRecipe",
          message: "Total nutrition for this recipe",
        }),

        caloriesPerServingMessage: function caloriesPerServingMessage(
          rawCalories: number,
          spanClassName?: string,
          emClassName?: string
        ) {
          const calories = Math.round(rawCalories);
          return (
            <Trans id="MealPlanner.nutritionalInfo.caloriesPerServingMessage">
              <span className={spanClassName}>
                <em className={emClassName}>{calories} kcal</em>
                <br />
                per serving
              </span>
            </Trans>
          );
        },
      },

      mealPlanMenu: {
        startMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.startMealPlanButtonLabel",
          message: "Start meal plan",
        }),
        stopMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.stopMealPlanButtonLabel",
          message: "Stop meal plan",
        }),
        shareMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.shareMealPlanButtonLabel",
          message: "Share meal plan",
        }),
        unshareMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.unshareMealPlanButtonLabel",
          message: "Unshare meal plan",
        }),
        copyMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.copyMealPlanButtonLabel",
          message: "Save a copy",
        }),
        editMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.editMealPlanButtonLabel",
          message: "Edit meal plan",
        }),
        ingredientsListButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.ingredientsListButtonLabel",
          message: "Ingredients list",
        }),
        nutritionalInfoButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.nutritionalInfoButtonLabel",
          message: "Nutrition",
        }),
        printRecipesButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.printRecipesButtonLabel",
          message: "Print",
        }),
        aboutMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.aboutMealPlanButtonLabel",
          message: "About meal plan",
        }),
        deleteMealPlanButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.deleteMealPlanButtonLabel",
          message: "Delete meal plan",
        }),
        servingsLabel: t({
          id: "MealPlanner.mealPlanMenu.servingsLabel",
          message: "Servings",
        }),
        mealPlanStartedLabel: t({
          id: "MealPlanner.mealPlanMenu.mealPlanStartedLabel",
          message: "Current",
        }),
        addToShoppingListButtonLabel: t({
          id: "MealPlanner.mealPlanMenu.addToShoppingListButtonLabel",
          message: "Add to shopping list",
        }),
        toggleToImperial: t({
          id: "MealPlanner.mealPlanMenu.toggleToImperial",
          message: "Change to imperial",
        }),
        toggleToMetric: t({
          id: "MealPlanner.mealPlanMenu.toggleToMetric",
          message: "Change to metric",
        }),
      },

      mealPlanInfoDialog: {
        dialogTitle: t({
          id: "MealPlanner.mealPlanInfoDialog.dialogTitle",
          message: "About this meal plan",
        }),
        createdByLabel: t({
          id: "MealPlanner.mealPlanInfoDialog.createdByLabel",
          message: "Created by",
          comment: "Heading for the authors section",
        }),
      },

      mealPlanIngredientsList: {
        dialogTitle: t({
          id: "MealPlanner.mealPlanIngredientsList.dialogTitle",
          message: "Meal plan ingredients list",
        }),
        ingredientsListLabel: t({
          id: "MealPlanner.mealPlanIngredientsList.ingredientsListLabel",
          message: "Ingredients list",
        }),
      },

      nutritionalInfoDialog: {
        dialogTitle: t({
          id: "MealPlanner.nutritionalInfoDialog.dialogTitle",
          message: "Nutrition",
        }),
      },

      editMealPlanDialog: {
        dialogTitle: t({
          id: "MealPlanner.editMealPlanDialog.dialogTitle",
          message: "Edit meal plan",
        }),
        mealPlanTitleLabel: t({
          id: "MealPlanner.editMealPlanDialog.mealPlanTitleLabel",
          message: "Header",
        }),
        mealPlanDescriptionLabel: t({
          id: "MealPlanner.editMealPlanDialog.mealPlanDescriptionLabel",
          message: "Description",
        }),
        confirmEditButtonLabel: t({
          id: "MealPlanner.editMealPlanDialog.confirmEditButtonLabel",
          message: "Save",
        }),
        cancelEditButtonLabel: t({
          id: "MealPlanner.editMealPlanDialog.cancelEditButtonLabel",
          message: "Cancel",
        }),
      },

      copyMealPlanDialog: {
        dialogTitle: t({
          id: "MealPlanner.copyMealPlanDialog.dialogTitle",
          message: "Add to my meal plans",
        }),
        whereToFindTheMealPlanCopyMessage: t({
          id: "MealPlanner.copyMealPlanDialog.",
          message:
            "This will save a copy to My meal plans. You can then easily find it later, activate it, and edit it.",
        }),
        mealPlanTitleLabel: t({
          id: "MealPlanner.copyMealPlanDialog.mealPlanTitleLabel",
          message: "Header",
        }),
        mealPlanDescriptionLabel: t({
          id: "MealPlanner.copyMealPlanDialog.mealPlanDescriptionLabel",
          message: "Description",
        }),
        confirmCopyButtonLabel: t({
          id: "MealPlanner.copyMealPlanDialog.confirmCopyButtonLabel",
          message: "Save",
        }),
        cancelCopyButtonLabel: t({
          id: "MealPlanner.copyMealPlanDialog.cancelCopyButtonLabel",
          message: "Cancel",
        }),
      },

      dayMenu: {
        addRecipeLabel: t({
          id: "MealPlanner.dayMenu.addRecipeLabel",
          message: "Add recipe to",
        }),
      },
      recipeCardMenu: {
        selectLabel: t({
          id: "MealPlanner.recipeCardMenu.selectLabel",
          message: "Select",
        }),
      },

      recipeMenu: {
        swapRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.swapRecipeButtonLabel",
          message: "Swap",
        }),
        copyRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.copyRecipeButtonLabel",
          message: "Copy to",
        }),
        viewRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.viewRecipeButtonLabel",
          message: "View recipe",
        }),
        favoriteButtonLabel: t({
          id: "MealPlanner.recipeMenu.favoriteButtonLabel",
          message: "Favorite",
        }),
        unfavoriteRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.unfavoriteRecipeButtonLabel",
          message: "Unfavorite",
        }),
        shareRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.shareRecipeButtonLabel",
          message: "Share",
        }),
        removeRecipeButtonLabel: t({
          id: "MealPlanner.recipeMenu.removeRecipeButtonLabel",
          message: "Remove",
        }),
        addToShoppingListButtonLabel: t({
          id: "MealPlanner.recipeMenu.addToShoppingListButtonLabel",
          message: "Add to shopping list",
        }),
      },

      recipePreviewDialog: {
        viewRecipeButtonLabel: t({
          id: "MealPlanner.recipePreviewDialog.viewRecipeButtonLabel",
          message: "Go to original recipe",
        }),
        leftoversMessage: (totalServingsCount: number, leftoverServingsCount: number) => {
          const totalServings = translations.quantities.servings(totalServingsCount);
          const leftoverServings = translations.quantities.servings(leftoverServingsCount);

          return t({
            id: "MealPlanner.recipePreviewDialog.leftoversMessage",
            message: `Remember to cook ${totalServings} and save ${leftoverServings} for tomorrow.`,
          });
        },
        mismatchServingsMessage: t({
          id: "MealPlanner.recipePreviewDialog.mismatchServingsMessage",
          message: `The servings are mismatching with the recipe's possible servings. Try to change the servings.`,
        }),
        nutritionSectionLabel: t({
          id: "MealPlanner.recipePreviewDialog.nutritionSectionLabel",
          message: "Nutrition",
        }),
        instructionsSectionLabel: t({
          id: "MealPlanner.recipePreviewDialog.instructionsSectionLabel",
          message: "Instructions",
        }),
        ingredientsSectionLabel: t({
          id: "MealPlanner.recipePreviewDialog.ingredientsSectionLabel",
          message: "Ingredients",
        }),
      },

      recipeSelectorDialog: {
        recipeSuggestionsTabLabel: t({
          id: "MealPlanner.recipeSelectorDialog.recipeSuggestionsTabLabel",
          message: "Suggestions",
        }),
        favoriteRecipesTabLabel: t({
          id: "MealPlanner.recipeSelectorDialog.favoriteRecipesTabLabel",
          message: "My favorites",
        }),
        searchRecipesTabLabel: t({
          id: "MealPlanner.recipeSelectorDialog.searchRecipesTabLabel",
          message: "Search",
        }),

        selectRecipeButtonLabel: t({
          id: "MealPlanner.recipeSelectorDialog.selectRecipeButtonLabel",
          message: "Select",
        }),
        newRecipeSuggestionButtonLabel: t({
          id: "MealPlanner.recipeSelectorDialog.newRecipeSuggestionButtonLabel",
          message: "New suggestion",
        }),
        showRecipesButtonLabel: (amount: number) =>
          t({
            id: "MealPlanner.recipeSelectorDialog.showRecipesButtonLabel",
            message: `Show ${amount} recipes`,
          }),
        searchInputPlaceholder: t({
          id: "MealPlanner.recipeSelectorDialog.searchInputPlaceholder",
          message: "Search",
        }),

        noFavoriteRecipesFoundMessage: t({
          id: "MealPlanner.recipeSelectorDialog.noFavoriteRecipesFoundMessage",
          message: "No favorited recipes found",
        }),
        noRecipesFoundMessage: t({
          id: "MealPlanner.recipeSelectorDialog.noRecipesFoundMessage",
          message: "No recipes found",
        }),
        loadMoreButtonTitle: t({
          id: "MealPlanner.recipeSelectorDialog.loadMoreButtonTitle",
          message: "More recipes",
        }),

        satietyScore: t({
          id: "MealPlanner.recipeSelectorDialog.satietyScore",
          message: "Satiety score",
        }),

        cookingTime: t({
          id: "MealPlanner.recipeSelectorDialog.cookingTime",
          message: "Cooking Time",
        }),

        tags: {
          type: t({
            id: "MealPlanner.recipeSelectorDialog.tags.type",
            message: "Type",
          }),
          protein: t({
            id: "MealPlanner.recipeSelectorDialog.tags.protein",
            message: "Protein",
          }),
          diet: t({
            id: "MealPlanner.recipeSelectorDialog.tags.diet",
            message: "Diet",
          }),
          strictness: t({
            id: "MealPlanner.recipeSelectorDialog.tags.strictness",
            message: "Strictness",
          }),
          difficulty: t({
            id: "MealPlanner.recipeSelectorDialog.tags.difficulty",
            message: "Difficulty",
          }),
          ingredient: t({
            id: "MealPlanner.recipeSelectorDialog.tags.ingredient",
            message: "Ingredient",
          }),
          tags: t({
            id: "MealPlanner.recipeSelectorDialog.tags.tags",
            message: "Tags",
          }),
          dish: t({
            id: "MealPlanner.recipeSelectorDialog.tags.dish",
            message: "Dish",
          }),

          keto: t({
            id: "MealPlanner.recipeSelector.tags.keto",
            message: "Ketogenic",
          }),
          moderate: t({
            id: "MealPlanner.recipeSelector.tags.moderate",
            message: "Moderate low carb",
          }),
          liberal: t({
            id: "MealPlanner.recipeSelector.tags.liberal",
            message: "Liberal low carb",
          }),

          beginner: t({
            id: "MealPlanner.recipeSelector.tags.beginner",
            message: "Beginner",
          }),
          easy: t({
            id: "MealPlanner.recipeSelector.tags.easy",
            message: "Easy",
          }),
          medium: t({
            id: "MealPlanner.recipeSelector.tags.medium",
            message: "Medium",
          }),
          hard: t({
            id: "MealPlanner.recipeSelector.tags.hard",
            message: "Hard",
          }),

          highProtein: t({
            id: "MealPlanner.recipeSelector.tags.highProtein",
            message: "High-protein",
          }),
          highProteinMainCourse: t({
            id: "MealPlanner.recipeSelector.tags.highProteinMainCourse",
            message: "High protein main course",
          }),
          highProteinMeal: t({
            id: "MealPlanner.recipeSelector.tags.highProteinMeal",
            message: "High protein meal",
          }),
          highProteinDinner: t({
            id: "MealPlanner.recipeSelector.tags.highProteinDinner",
            message: "High protein dinner",
          }),
          highProteinLunch: t({
            id: "MealPlanner.recipeSelector.tags.highProteinLunch",
            message: "High protein lunch",
          }),
          highProteinBreakfast: t({
            id: "MealPlanner.recipeSelector.tags.highProteinBreakfast",
            message: "High protein breakfast",
          }),
          highProteinSnack: t({
            id: "MealPlanner.recipeSelector.tags.highProteinSnack",
            message: "High protein snack",
          }),
          highProteinDessert: t({
            id: "MealPlanner.recipeSelector.tags.highProteinDessert",
            message: "High protein dessert",
          }),
          highProteinBread: t({
            id: "MealPlanner.recipeSelector.tags.highProteinBread",
            message: "High protein bread",
          }),
          highProteinAppetizer: t({
            id: "MealPlanner.recipeSelector.tags.highProteinAppetizer",
            message: "High protein appetizer",
          }),

          soy: t({
            id: "MealPlanner.recipeSelector.tags.soy",
            message: "Soy",
          }),
          nuts: t({
            id: "MealPlanner.recipeSelector.tags.nuts",
            message: "Nuts",
          }),
          dairy: t({
            id: "MealPlanner.recipeSelector.tags.dairy",
            message: "Dairy",
          }),
          egg: t({
            id: "MealPlanner.recipeSelector.tags.egg",
            message: "Egg",
          }),
          fish: t({
            id: "MealPlanner.recipeSelector.tags.fish",
            message: "Fish",
          }),
          meat: t({
            id: "MealPlanner.recipeSelector.tags.meat",
            message: "Meat",
          }),
          beef: t({
            id: "MealPlanner.recipeSelector.tags.beef",
            message: "Beef",
          }),
          game: t({
            id: "MealPlanner.recipeSelector.tags.game",
            message: "Game",
          }),
          lamb: t({
            id: "MealPlanner.recipeSelector.tags.lamb",
            message: "Lamb",
          }),
          pork: t({
            id: "MealPlanner.recipeSelector.tags.pork",
            message: "Pork",
          }),
          poultry: t({
            id: "MealPlanner.recipeSelector.tags.poultry",
            message: "Poultry",
          }),
          chicken: t({
            id: "MealPlanner.recipeSelector.tags.chicken",
            message: "Chicken",
          }),
          turkey: t({
            id: "MealPlanner.recipeSelector.tags.turkey",
            message: "Turkey",
          }),
          duck: t({
            id: "MealPlanner.recipeSelector.tags.duck",
            message: "Duck",
          }),
          seafood: t({
            id: "MealPlanner.recipeSelector.tags.seafood",
            message: "Seafood",
          }),
        },
      },

      loadingDialog: {
        startingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.startingMealPlanMessage",
          message: "Starting meal plan...",
        }),
        stoppingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.stoppingMealPlanMessage",
          message: "Stopping meal plan...",
        }),
        copyingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.copyingMealPlanMessage",
          message: "Saving a copy...",
        }),
        editingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.editingMealPlanMessage",
          message: "Applying changes...",
        }),
        sharingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.sharingMealPlanMessage",
          message: "Sharing meal plan...",
        }),
        unsharingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.unsharingMealPlanMessage",
          message: "Unsharing meal plan...",
        }),
        addingRecipeToMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.addingRecipeToMealPlanMessage",
          message: "Adding recipe to meal plan...",
        }),
        removingRecipeToMealPlan: t({
          id: "MealPlanner.loadingDialog.removingRecipeToMealPlan",
          message: "Removing recipe to meal plan...",
        }),
        deletingMealPlanMessage: t({
          id: "MealPlanner.loadingDialog.deletingMealPlanMessage",
          message: "Deleting meal plan...",
        }),
        addingRecipesToShoppingListMessage: t({
          id: "MealPlanner.loadingDialog.addingRecipesToShoppingListMessage",
          message: "Adding recipes...",
        }),
      },

      confirmationDialog: {
        confirmMessageButtonLabel: t({
          id: "MealPlanner.confirmationDialog.confirmMessageButtonLabel",
          message: "Ok",
        }),

        defaultMessage: {
          title: t({
            id: "MealPlanner.confirmationDialog.defaultMessage.title",
            message: "Success",
            comment:
              "Title for the default message, which is used when there is no message associated for the current action",
          }),
        },

        messages: {
          [MealPlannerConfirmationMessage.MEAL_PLAN_STARTED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanStartedMessage.title",
              message: "Meal plan started",
            }),
            description: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanStartedMessage.description",
              message: "You’ve activated this meal plan.",
            }),
          },
          [MealPlannerConfirmationMessage.MEAL_PLAN_STOPPED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanStoppedMessage.title",
              message: "Meal plan stopped",
            }),
            description: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanStoppedMessage.description",
              message: "The meal plan is now stopped.",
            }),
          },
          [MealPlannerConfirmationMessage.MEAL_PLAN_SHARED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanSharedMessage.title",
              message: "This meal plan can now be shared",
            }),
          },
          [MealPlannerConfirmationMessage.MEAL_PLAN_UNSHARED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanUnsharedMessage.title",
              message: "Unshare meal plan",
            }),
            description: t({
              id: "MealPlanner.confirmationDialog.messages.mealPlanUnsharedMessage.description",
              message: "This meal plan is now private.",
            }),
          },
          [MealPlannerConfirmationMessage.RECIPE_SHARED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.recipeSharedMessage.title",
              message: "This recipe can now be shared",
            }),
          },
          [MealPlannerConfirmationMessage.MEALPLAN_DELETED]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.mealplanDeletedMessage.title",
              message: "This mealplan is now deleted",
            }),
          },
          [MealPlannerConfirmationMessage.RECIPES_ADDED_TO_SHOPPING_LIST]: {
            title: t({
              id: "MealPlanner.confirmationDialog.messages.recipesAddedToShoppingListMessage.title",
              message: "The recipes have been added to your shopping list",
            }),
          },
        },
      },

      errorDialog: {
        confirmErrorButtonLabel: t({
          id: "MealPlanner.errorDialog.confirmErrorButtonLabel",
          message: "Ok",
        }),

        defaultMessage: {
          title: t({
            id: "MealPlanner.errorDialog.defaultMessage.title",
            message: "Something went wrong",
          }),
          description: t({
            id: "MealPlanner.errorDialog.defaultMessage.description",
            message: "Please try again later.",
          }),
        },

        messages: {
          CAN_NOT_START_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotStartMealPlanError.title",
              message: `Can't start meal plan`,
            }),
          },
          FAILED_TO_START_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.failedToStartMealPlanError.title",
              message: `Can't start meal plan`,
            }),
          },
          CAN_NOT_STOP_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotStopMealPlanError.title",
              message: `Can't stop meal plan`,
            }),
          },
          FAILED_TO_STOP_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.failedToStopMealPlanError.title",
              message: `Can't stop meal plan`,
            }),
          },
          CAN_NOT_COPY_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotCopyMealPlanError.title",
              message: `Can't save a copy`,
            }),
          },
          FAILED_TO_COPY_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotCopyMealPlanError.title",
              message: `Can't save a copy`,
            }),
          },
          CAN_NOT_EDIT_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotEditMealPlanError.title",
              message: `Can't edit meal plan`,
            }),
          },
          FAILED_TO_EDIT_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotEditMealPlanError.title",
              message: `Can't edit meal plan`,
            }),
          },
          CAN_NOT_SHARE_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotShareMealPlanError.title",
              message: `Can't share meal plan`,
            }),
          },
          FAILED_TO_SHARE_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.failedToShareMealPlanError.title",
              message: `Can't share meal plan`,
            }),
          },
          CAN_NOT_UNSHARE_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotUnshareMealPlanError.title",
              message: `Can't unshare meal plan`,
            }),
          },
          FAILED_TO_UNSHARE_MEAL_PLAN: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotUnshareMealPlanError.title",
              message: `Can't unshare meal plan`,
            }),
          },
          CAN_NOT_ADD_RECIPE: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotAddRecipeError.title",
              message: `Can't add recipe`,
            }),
          },
          FAILED_TO_ADD_RECIPE: {
            title: t({
              id: "MealPlanner.errorDialog.messages.failedToAddRecipeError.title",
              message: `Can't add recipe`,
            }),
          },
          CAN_NOT_REMOVE_RECIPE: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotRemoveRecipeError.title",
              message: `Can't remove recipe`,
            }),
          },
          FAILED_TO_REMOVE_RECIPE: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotRemoveRecipeError.title",
              message: `Can't remove recipe`,
            }),
          },
          TOO_MANY_RECIPES: {
            title: t({
              id: "MealPlanner.errorDialog.messages.tooManyRecipesError.title",
              message: `You can't add more than 3 recipes per meal`,
            }),
          },
          CAN_NOT_ADD_RECIPES_SHOPPING_LIST: {
            title: t({
              id: "MealPlanner.errorDialog.messages.canNotAddRecipesShoppingList.title",
              message: `No recipes to add into your shopping list`,
            }),
          },
        } as Partial<Record<MealPlannerErrorCode, Message>>,
      },

      actionConfirmationDialog: {
        confirmButtonLabel: t({
          id: "MealPlanner.actionConfirmationDialog.confirmButtonLabel",
          message: "Ok",
        }),
        cancelButtonLabel: t({
          id: "MealPlanner.actionConfirmationDialog.cancelButtonLabel",
          message: "Cancel",
        }),

        updateMealPlanServings: {
          title: t({
            id: "MealPlanner.actionConfirmationDialog.updateMealPlanServings.title",
            message: "Confirm change",
          }),
          message: t({
            id: "MealPlanner.actionConfirmationDialog.updateMealPlanServings.message",
            message: "This action will override the servings of all of your recipes. Do you want to proceed?",
          }),
        },
        updateMealPlanDayServings: {
          title: t({
            id: "MealPlanner.actionConfirmationDialog.updateMealPlanDayServings.title",
            message: "Confirm change",
          }),
          message: t({
            id: "MealPlanner.actionConfirmationDialog.updateMealPlanDayServings.message",
            message:
              "This action will override the servings of all of your recipes for the selected day. Do you want to proceed?",
          }),
        },
      },

      printableRecipes: {
        noRecipesMessage: t({
          id: "MealPlanner.printableRecipes.noRecipesMessage",
          message: "No recipes in meal plan!",
        }),
      },

      nextMealPlan: t({
        id: "MealPlanner.printableRecipes.nextMealPlan",
        message: "Next meal plan",
      }),
    };

    return translations;
  }, [locale]);
}

export type MealPlannerTranslations = ReturnType<typeof useMealPlannerTranslations>;

export const MealPlannerTranslationsContext = React.createContext<MealPlannerTranslations>({
  pageTitle: "",

  defaultMealPlanTitle: "",
  heroImageDescription: "",

  currentMealPlanTitle: "",

  toolbar: {
    startMealPlanButtonLabel: "",
    copyMealPlanButtonLabel: "",
    mealPlanStartedLabel: "",
  },

  energyLevel: {
    [EnergyLevel.HIGH_PROTEIN_KETO]: "",
    [EnergyLevel.HIGH_PROTEIN_LOW_CARB]: "",
    [EnergyLevel.HIGH_PROTEIN_LIBERAL]: "",
    [EnergyLevel.KETO]: "",
    [EnergyLevel.LOW_CARB]: "",
    [EnergyLevel.LIBERAL]: "",
  },

  quantities: {
    amountInGrams: () => "",
    percentage: () => "",
    timeInMinutes: () => "",
    servings: () => "",
    servingsWithLeftovers: () => "",
  },

  dates: {
    weekdayLong: () => "",
  },

  meals: {
    mealLabel: () => "",
  },

  mealPlanGrid: {
    addMealButtonLabel: "",
    missingRecipePlaceholderTitle: "",
  },

  nutritionalInfo: {
    nutrients: {
      netCarbs: "",
      totalCarbs: "",
      protein: "",
      fat: "",
      fiber: "",
      calories: "",
    },

    averageNutritionPerDayLabel: "",
    totalNutritionForDayLabel: "",
    totalNutritionForRecipe: "",

    caloriesPerServingMessage: function noop() {
      return <span />;
    },
  },

  mealPlanMenu: {
    startMealPlanButtonLabel: "",
    stopMealPlanButtonLabel: "",
    shareMealPlanButtonLabel: "",
    unshareMealPlanButtonLabel: "",
    deleteMealPlanButtonLabel: "",
    copyMealPlanButtonLabel: "",
    editMealPlanButtonLabel: "",
    ingredientsListButtonLabel: "",
    nutritionalInfoButtonLabel: "",
    printRecipesButtonLabel: "",
    aboutMealPlanButtonLabel: "",
    servingsLabel: "",
    mealPlanStartedLabel: "",
    addToShoppingListButtonLabel: "",
    toggleToImperial: "",
    toggleToMetric: "",
  },

  mealPlanInfoDialog: {
    dialogTitle: "",
    createdByLabel: "",
  },

  mealPlanIngredientsList: {
    dialogTitle: "",
    ingredientsListLabel: "",
  },

  nutritionalInfoDialog: {
    dialogTitle: "",
  },

  editMealPlanDialog: {
    dialogTitle: "",
    mealPlanTitleLabel: "",
    mealPlanDescriptionLabel: "",
    confirmEditButtonLabel: "",
    cancelEditButtonLabel: "",
  },

  copyMealPlanDialog: {
    dialogTitle: "",
    whereToFindTheMealPlanCopyMessage: "",
    mealPlanTitleLabel: "",
    mealPlanDescriptionLabel: "",
    confirmCopyButtonLabel: "",
    cancelCopyButtonLabel: "",
  },

  dayMenu: {
    addRecipeLabel: "",
  },

  recipeMenu: {
    swapRecipeButtonLabel: "",
    copyRecipeButtonLabel: "",
    viewRecipeButtonLabel: "",
    favoriteButtonLabel: "",
    unfavoriteRecipeButtonLabel: "",
    shareRecipeButtonLabel: "",
    removeRecipeButtonLabel: "",
    addToShoppingListButtonLabel: "",
  },
  recipeCardMenu: {
    selectLabel: "",
  },

  recipePreviewDialog: {
    viewRecipeButtonLabel: "",
    leftoversMessage: () => "",
    mismatchServingsMessage: "",
    nutritionSectionLabel: "",
    instructionsSectionLabel: "",
    ingredientsSectionLabel: "",
  },

  recipeSelectorDialog: {
    recipeSuggestionsTabLabel: "",
    favoriteRecipesTabLabel: "",
    searchRecipesTabLabel: "",
    loadMoreButtonTitle: "",
    selectRecipeButtonLabel: "",
    newRecipeSuggestionButtonLabel: "",
    showRecipesButtonLabel: () => "",
    searchInputPlaceholder: "",
    satietyScore: "",
    cookingTime: "",

    noFavoriteRecipesFoundMessage: "",
    noRecipesFoundMessage: "",

    tags: {
      type: "",
      protein: "",
      diet: "",
      strictness: "",
      difficulty: "",
      ingredient: "",
      tags: "",
      dish: "",

      keto: "",
      moderate: "",
      liberal: "",

      beginner: "",
      easy: "",
      medium: "",
      hard: "",

      highProtein: "",
      highProteinMainCourse: "",
      highProteinMeal: "",
      highProteinDinner: "",
      highProteinLunch: "",
      highProteinBreakfast: "",
      highProteinSnack: "",
      highProteinDessert: "",
      highProteinBread: "",
      highProteinAppetizer: "",

      soy: "",
      nuts: "",
      dairy: "",
      egg: "",
      fish: "",
      meat: "",
      beef: "",
      game: "",
      lamb: "",
      pork: "",
      poultry: "",
      chicken: "",
      turkey: "",
      duck: "",
      seafood: "",
    },
  },

  loadingDialog: {
    startingMealPlanMessage: "",
    stoppingMealPlanMessage: "",
    copyingMealPlanMessage: "",
    editingMealPlanMessage: "",
    sharingMealPlanMessage: "",
    unsharingMealPlanMessage: "",
    addingRecipeToMealPlanMessage: "",
    removingRecipeToMealPlan: "",
    deletingMealPlanMessage: "",
    addingRecipesToShoppingListMessage: "",
  },

  confirmationDialog: {
    confirmMessageButtonLabel: "",

    defaultMessage: {
      title: "",
    },

    messages: {
      MEAL_PLAN_STARTED: {
        title: "",
        description: "",
      },
      MEAL_PLAN_STOPPED: {
        title: "",
        description: "",
      },
      MEAL_PLAN_SHARED: {
        title: "",
      },
      MEAL_PLAN_UNSHARED: {
        title: "",
        description: "",
      },
      RECIPE_SHARED: {
        title: "",
      },
      MEALPLAN_DELETED: {
        title: "",
      },
      RECIPES_ADDED_TO_SHOPPING_LIST: {
        title: "",
      },
    },
  },

  errorDialog: {
    confirmErrorButtonLabel: "",

    defaultMessage: {
      title: "",
      description: "",
    },

    messages: {
      CAN_NOT_START_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_START_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_STOP_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_STOP_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_COPY_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_COPY_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_EDIT_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_EDIT_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_SHARE_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_SHARE_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_UNSHARE_MEAL_PLAN: {
        title: "",
      },
      FAILED_TO_UNSHARE_MEAL_PLAN: {
        title: "",
      },
      CAN_NOT_ADD_RECIPE: {
        title: "",
      },
      FAILED_TO_ADD_RECIPE: {
        title: "",
      },
      CAN_NOT_REMOVE_RECIPE: {
        title: "",
      },
      FAILED_TO_REMOVE_RECIPE: {
        title: "",
      },
    },
  },

  actionConfirmationDialog: {
    confirmButtonLabel: "",
    cancelButtonLabel: "",

    updateMealPlanServings: {
      title: "",
      message: "",
    },
    updateMealPlanDayServings: {
      title: "",
      message: "",
    },
  },

  printableRecipes: {
    noRecipesMessage: "",
  },

  nextMealPlan: "",
});
